/**
 * 公式評估器 - 前後端共用模組
 * 
 * @module lib/shared/formula-evaluator
 * @description 提供表單公式計算的核心邏輯
 * 
 * 使用場景：
 * - 前端：即時計算欄位值預覽
 * - 後端：表單提交時計算欄位值
 * 
 * @example
 * import { evaluateFormula } from '@/lib/shared/formula-evaluator';
 * const result = evaluateFormula('sum(field1, field2)', formData);
 */

/**
 * 解析日期字串為本地日期
 * @param {string} dateStr - 日期字串
 * @returns {Date} 日期物件
 */
function parseLocalDate(dateStr) {
    if (typeof dateStr === 'string') {
        const m = dateStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
        if (m) {
            return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
        }
    }
    return new Date(dateStr);
}

/**
 * 計算兩個日期之間的天數差
 * @param {string|Date} startDate - 開始日期
 * @param {string|Date} endDate - 結束日期
 * @param {boolean} [inclusive=true] - 是否包含首尾日期（預設為 true）
 * @returns {number} 天數差
 * 
 * @example
 * // 2024-01-01 到 2024-01-03，包含首尾 = 3 天
 * daysBetween('2024-01-01', '2024-01-03'); // 3
 * daysBetween('2024-01-01', '2024-01-03', false); // 2
 */
export function daysBetween(startDate, endDate, inclusive = true) {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diff = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return inclusive ? diff + 1 : diff;
}

/**
 * 加總多個數值
 * @param {...number} values - 要加總的數值
 * @returns {number} 總和
 */
export function sum(...values) {
    return values.reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
}

/**
 * 計算多個數值的乘積
 * @param {...number} values - 要相乘的數值
 * @returns {number} 乘積
 */
export function multiply(...values) {
    if (values.length === 0) return 0;
    return values.reduce((acc, val) => acc * (parseFloat(val) || 0), 1);
}

/**
 * 計算平均值
 * @param {...number} values - 要計算平均的數值
 * @returns {number} 平均值
 */
export function average(...values) {
    if (values.length === 0) return 0;
    return sum(...values) / values.length;
}

/**
 * 取最大值
 * @param {...number} values - 要比較的數值
 * @returns {number} 最大值
 */
export function max(...values) {
    if (values.length === 0) return 0;
    return Math.max(...values.map(v => parseFloat(v) || 0));
}

/**
 * 取最小值
 * @param {...number} values - 要比較的數值
 * @returns {number} 最小值
 */
export function min(...values) {
    if (values.length === 0) return 0;
    return Math.min(...values.map(v => parseFloat(v) || 0));
}

/**
 * 四捨五入
 * @param {number} value - 要四捨五入的數值
 * @param {number} [decimals=0] - 小數位數
 * @returns {number} 四捨五入後的數值
 */
export function round(value, decimals = 0) {
    const factor = Math.pow(10, decimals);
    return Math.round((parseFloat(value) || 0) * factor) / factor;
}

/**
 * 無條件捨去
 * @param {number} value - 要捨去的數值
 * @param {number} [decimals=0] - 小數位數
 * @returns {number} 捨去後的數值
 */
export function floor(value, decimals = 0) {
    const factor = Math.pow(10, decimals);
    return Math.floor((parseFloat(value) || 0) * factor) / factor;
}

/**
 * 無條件進位
 * @param {number} value - 要進位的數值
 * @param {number} [decimals=0] - 小數位數
 * @returns {number} 進位後的數值
 */
export function ceil(value, decimals = 0) {
    const factor = Math.pow(10, decimals);
    return Math.ceil((parseFloat(value) || 0) * factor) / factor;
}

/**
 * 條件判斷
 * @param {boolean} condition - 條件
 * @param {*} trueValue - 條件為真時的值
 * @param {*} falseValue - 條件為假時的值
 * @returns {*} 結果值
 */
export function ifThen(condition, trueValue, falseValue) {
    return condition ? trueValue : falseValue;
}

/**
 * 內建函數映射表
 */
const FORMULA_FUNCTIONS = {
    sum,
    multiply,
    average,
    avg: average,
    max,
    min,
    round,
    floor,
    ceil,
    daysBetween,
    if: ifThen,
    abs: (v) => Math.abs(parseFloat(v) || 0),
    sqrt: (v) => Math.sqrt(parseFloat(v) || 0),
    pow: (base, exp) => Math.pow(parseFloat(base) || 0, parseFloat(exp) || 0),
};

/**
 * 評估公式
 * @param {string} formula - 公式字串
 * @param {Object} formData - 表單數據
 * @returns {*} 計算結果
 * 
 * @example
 * // 支援的公式格式：
 * evaluateFormula('sum(field1, field2, field3)', formData);
 * evaluateFormula('multiply(price, quantity)', formData);
 * evaluateFormula('daysBetween(start_date, end_date)', formData);
 * evaluateFormula('daysBetween(start_date, end_date) + 1', formData);
 * evaluateFormula('round(total * 0.1, 2)', formData);
 */
export function evaluateFormula(formula, formData) {
    try {
        if (!formula || typeof formula !== 'string') {
            return undefined;
        }
        
        // sum(field1, field2, ...)
        if (formula.startsWith('sum(')) {
            const match = formula.match(/sum\(([^)]+)\)/);
            if (match) {
                const fields = match[1].split(',').map(f => f.trim());
                return fields.reduce((acc, f) => acc + (parseFloat(formData[f]) || 0), 0);
            }
        }
        
        // multiply(field1, field2, ...)
        if (formula.startsWith('multiply(')) {
            const match = formula.match(/multiply\(([^)]+)\)/);
            if (match) {
                const fields = match[1].split(',').map(f => f.trim());
                return fields.reduce((acc, f) => {
                    const val = parseFloat(formData[f]) || 0;
                    return acc * val;
                }, 1);
            }
        }
        
        // average(field1, field2, ...)
        if (formula.startsWith('average(') || formula.startsWith('avg(')) {
            const match = formula.match(/(?:average|avg)\(([^)]+)\)/);
            if (match) {
                const fields = match[1].split(',').map(f => f.trim());
                const values = fields.map(f => parseFloat(formData[f]) || 0);
                return values.reduce((a, b) => a + b, 0) / values.length;
            }
        }
        
        // max(field1, field2, ...)
        if (formula.startsWith('max(')) {
            const match = formula.match(/max\(([^)]+)\)/);
            if (match) {
                const fields = match[1].split(',').map(f => f.trim());
                const values = fields.map(f => parseFloat(formData[f]) || 0);
                return Math.max(...values);
            }
        }
        
        // min(field1, field2, ...)
        if (formula.startsWith('min(')) {
            const match = formula.match(/min\(([^)]+)\)/);
            if (match) {
                const fields = match[1].split(',').map(f => f.trim());
                const values = fields.map(f => parseFloat(formData[f]) || 0);
                return Math.min(...values);
            }
        }
        
        // daysBetween(startDate, endDate)
        if (formula.startsWith('daysBetween(')) {
            const match = formula.match(/daysBetween\(([^,]+),\s*([^)]+)\)/);
            if (match) {
                const startDateStr = formData[match[1].trim()];
                const endDateStr = formData[match[2].trim()];
                if (startDateStr && endDateStr) {
                    return daysBetween(startDateStr, endDateStr);
                }
            }
        }
        
        // daysBetween(startDate, endDate) + N
        if (formula.includes('daysBetween(') && formula.includes('+')) {
            const match = formula.match(/daysBetween\(([^,]+),\s*([^)]+)\)\s*\+\s*(\d+)/);
            if (match) {
                const startDateStr = formData[match[1].trim()];
                const endDateStr = formData[match[2].trim()];
                const add = parseInt(match[3]) || 0;
                if (startDateStr && endDateStr) {
                    return daysBetween(startDateStr, endDateStr) + add;
                }
            }
        }
        
        // round(expression, decimals)
        if (formula.startsWith('round(')) {
            const match = formula.match(/round\(([^,]+),?\s*(\d*)\)/);
            if (match) {
                const value = parseFloat(formData[match[1].trim()]) || 0;
                const decimals = parseInt(match[2]) || 0;
                return round(value, decimals);
            }
        }
        
        // 簡單的算術表達式 (純數字運算)
        if (/^[\d+\-*/().\s]+$/.test(formula)) {
            // 使用 Function 構造器執行簡單數學運算
            // 注意：這在瀏覽器和 Node.js 環境都可用
            return new Function(`return ${formula}`)();
        }
        
        // 替換欄位名稱後的算術表達式
        let evaluableFormula = formula;
        const fieldMatches = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g);
        if (fieldMatches) {
            for (const fieldName of fieldMatches) {
                if (formData.hasOwnProperty(fieldName)) {
                    const value = parseFloat(formData[fieldName]) || 0;
                    evaluableFormula = evaluableFormula.replace(
                        new RegExp(`\\b${fieldName}\\b`, 'g'),
                        value
                    );
                }
            }
            // 檢查替換後是否為有效的算術表達式
            if (/^[\d+\-*/().\s]+$/.test(evaluableFormula)) {
                return new Function(`return ${evaluableFormula}`)();
            }
        }
        
        return undefined;
    } catch (err) {
        console.error('Error evaluating formula:', formula, err);
        return undefined;
    }
}

/**
 * 執行觸發的計算
 * @param {Object} calculations - 計算配置物件
 * @param {Object} formData - 表單數據
 * @param {string} [changedFieldId] - 變更的欄位 ID
 * @returns {{ updatedFormData: Object, changedFields: string[] }} 更新後的表單數據和變更的欄位
 */
export function executeCalculations(calculations, formData, changedFieldId = null) {
    let updatedFormData = { ...formData };
    const changedFields = [];
    
    // 找出被觸發的計算
    const triggeredCalculations = [];
    for (const [calcName, calc] of Object.entries(calculations || {})) {
        if (!changedFieldId || calc.trigger?.includes(changedFieldId)) {
            triggeredCalculations.push({ name: calcName, ...calc });
        }
    }
    
    // 執行計算
    for (const calc of triggeredCalculations) {
        if (!calc.target || !calc.formula) continue;
        
        const result = evaluateFormula(calc.formula, updatedFormData);
        if (result !== undefined && result !== updatedFormData[calc.target]) {
            updatedFormData[calc.target] = result;
            changedFields.push(calc.target);
        }
    }
    
    return { updatedFormData, changedFields };
}

/**
 * 獲取計算欄位的觸發來源
 * @param {Object} calculations - 計算配置物件
 * @param {string} targetField - 目標欄位
 * @returns {string[]} 觸發來源欄位列表
 */
export function getCalculationTriggers(calculations, targetField) {
    for (const calc of Object.values(calculations || {})) {
        if (calc.target === targetField) {
            return calc.trigger || [];
        }
    }
    return [];
}

/**
 * 獲取欄位會觸發的計算目標
 * @param {Object} calculations - 計算配置物件
 * @param {string} fieldId - 欄位 ID
 * @returns {string[]} 計算目標欄位列表
 */
export function getCalculationTargets(calculations, fieldId) {
    const targets = [];
    for (const calc of Object.values(calculations || {})) {
        if (calc.trigger?.includes(fieldId) && calc.target) {
            targets.push(calc.target);
        }
    }
    return targets;
}

// 導出內建函數供進階使用
export const FORMULA_FUNCTIONS_MAP = FORMULA_FUNCTIONS;

export default {
    evaluateFormula,
    executeCalculations,
    getCalculationTriggers,
    getCalculationTargets,
    daysBetween,
    sum,
    multiply,
    average,
    max,
    min,
    round,
    floor,
    ceil,
    ifThen,
    FORMULA_FUNCTIONS_MAP
};
